import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowRight, ShieldCheck } from "lucide-react";
import { useLocation } from "@/contexts/LocationContext";
import { getOtherLocation } from "@/lib/locations";
import { formatPhone } from "@/lib/utils";
import dsEmblem from "@/assets/logos/ds-emblem.webp";

export function WarmFooter() {
  const { location, locationId, isSeattle } = useLocation();
  const otherLocation = getOtherLocation(locationId);
  const basePath = isSeattle ? "/seattle" : "";
  const otherBasePath = otherLocation.id === "seattle" ? "/seattle" : "";
  const linkCls =
    "text-sm text-primary-foreground/70 transition-colors hover:text-[hsl(var(--accent-on-dark))]";

  return (
    <footer className="band-deep grain relative">
      <div className="container relative mx-auto px-4 py-16">
        <div className="grid gap-12 md:grid-cols-3">
          {/* Brand — the logo badge anchors the footer. */}
          <div>
            <div className="flex items-center gap-3">
              <img
                src={dsEmblem}
                alt="Dawit Solomon Adult Family Home"
                className="h-11 w-11 brightness-0 invert"
              />
              <p className="font-heading text-lg font-semibold leading-tight text-primary-foreground">
                Dawit Solomon
                <span className="block text-xs font-medium uppercase tracking-[0.16em] text-primary-foreground/60">
                  Adult Family Home
                </span>
              </p>
            </div>
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-primary-foreground/70">
              A licensed home where your family becomes part of ours — compassionate,
              personal care in a warm, real-home setting.
            </p>

            <Link
              to={`${otherBasePath}/`}
              className="group mt-5 inline-flex items-center gap-2 rounded-full border border-[hsl(var(--accent-on-dark))]/30 px-4 py-2 text-sm font-semibold text-[hsl(var(--accent-on-dark))] transition-colors hover:bg-[hsl(var(--accent-on-dark))]/10"
            >
              <MapPin className="h-4 w-4 transition-transform group-hover:scale-110" />
              <span>Also serving {otherLocation.shortName}</span>
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>

          {/* Quick Links */}
          <div>
            <h2 className="eyebrow text-[hsl(var(--accent-on-dark))]">Explore</h2>
            <nav aria-label="Footer" className="mt-5 flex flex-col gap-3">
              <Link to={`${basePath}/about`} className={linkCls}>Our Story</Link>
              <Link to={`${basePath}/gallery`} className={linkCls}>Our Home</Link>
              <Link to={`${basePath}/testimonials`} className={linkCls}>Family Stories</Link>
              <Link to={`${basePath}/services`} className={linkCls}>Care &amp; Amenities</Link>
              <Link to={`${basePath}/contact`} className={linkCls}>Schedule a Visit</Link>
            </nav>
          </div>

          {/* Contact — call-first, then the full NAP for credibility. */}
          <div>
            <h2 className="eyebrow text-[hsl(var(--accent-on-dark))]">Get in Touch</h2>
            <div className="mt-5 flex flex-col gap-4">
              <a
                href={`tel:${location.primaryPhone.replace(/-/g, "")}`}
                className="inline-flex w-fit items-center gap-2 rounded-full bg-accent px-5 py-3 text-sm font-semibold text-accent-foreground shadow-md transition-transform active:scale-[0.98]"
              >
                <Phone className="h-4 w-4" />
                Call {formatPhone(location.primaryPhone)}
              </a>
              {location.secondaryPhone && (
                <a
                  href={`tel:${location.secondaryPhone.replace(/-/g, "")}`}
                  className="text-xs text-primary-foreground/60 transition-colors hover:text-[hsl(var(--accent-on-dark))]"
                >
                  Alternate line: {formatPhone(location.secondaryPhone)}
                </a>
              )}
              <a
                href={`mailto:${location.email}`}
                className="flex items-center gap-3 text-sm text-primary-foreground/70 transition-colors hover:text-[hsl(var(--accent-on-dark))]"
              >
                <Mail className="h-4 w-4 text-[hsl(var(--accent-on-dark))]" />
                {location.email}
              </a>
              <p className="flex items-start gap-3 text-sm text-primary-foreground/70">
                <MapPin className="h-4 w-4 shrink-0 text-[hsl(var(--accent-on-dark))]" />
                <span>
                  {location.address}
                  <br />
                  {location.cityState}
                </span>
              </p>
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center gap-3 border-t border-white/10 pt-8 text-center">
          <span className="inline-flex items-center gap-2 text-xs font-medium text-primary-foreground/70">
            <ShieldCheck className="h-3.5 w-3.5 text-[hsl(var(--accent-on-dark))]" />
            {isSeattle ? "WA DSHS Licensed" : `WA State License #${location.licenseNumber}`}
            <span className="text-primary-foreground/40">·</span>
            Provider: {location.providerName}
          </span>
          <nav aria-label="Legal" className="flex items-center gap-4 text-xs">
            <Link to={`${basePath}/privacy`} className="text-primary-foreground/75 underline-offset-2 hover:text-[hsl(var(--accent-on-dark))] hover:underline">
              Privacy
            </Link>
            <span aria-hidden="true" className="text-primary-foreground/40">·</span>
            <Link to={`${basePath}/terms`} className="text-primary-foreground/75 underline-offset-2 hover:text-[hsl(var(--accent-on-dark))] hover:underline">
              Terms
            </Link>
          </nav>
          <p className="text-xs text-primary-foreground/55">
            © {new Date().getFullYear()} Dawit Solomon Adult Family Home LLC. Made with care for families like yours.
          </p>
          <p className="text-xs text-primary-foreground/55">
            Powered by{" "}
            <a
              href="https://lynxbrydge.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[hsl(var(--accent-on-dark))] transition-all duration-300 hover:drop-shadow-[0_0_8px_hsl(var(--accent)/0.6)]"
            >
              Lynx Brydge
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
